// app/providers.tsx
"use client";
import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { PostHogProvider as PostHogProviderBase } from "posthog-js/react";
import { useUser } from "@clerk/nextjs";
import { useSearchParams } from "next/navigation";

export type PostHogConfig = {
  postHogKey?: string;
  postHogHost?: string;
};

export function PostHogProvider({
  postHogConfig,
  children,
}: {
  postHogConfig?: PostHogConfig;
  children: React.ReactNode;
}) {
  const { user, isLoaded } = useUser();
  const searchParams = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize PostHog in useEffect to prevent hydration mismatches
  useEffect(() => {
    if (typeof window !== "undefined" && !isInitialized) {
      if (
        postHogConfig &&
        postHogConfig.postHogKey &&
        postHogConfig.postHogHost
      ) {
        posthog.init(postHogConfig.postHogKey, {
          api_host: postHogConfig.postHogHost,
          person_profiles: "identified_only",
          capture_pageview: false, // Disable automatic pageview capture, as we capture manually
          bootstrap: {
            sessionID: sessionId ?? undefined,
          },
        });
        setIsInitialized(true);
      }
    }
  }, [postHogConfig, sessionId, isInitialized]);

  // Identify user after initialization with email for feature flag targeting
  useEffect(() => {
    if (isInitialized && isLoaded && user?.id) {
      const email = user.primaryEmailAddress?.emailAddress;
      posthog.identify(user.id, {
        email: email,
        name: user.fullName,
      });
    }
  }, [user, isLoaded, isInitialized]);

  return <PostHogProviderBase client={posthog}>{children}</PostHogProviderBase>;
}
